import { Checkbox } from "@jugl-web/ui-kit";
import type { ConnectionDto, AgentTool } from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import React, { useMemo } from "react";
import { buildIntegrationAgentTool } from "../../../../utils/agentTool";

type IntegrationPack = "shopify" | "calendly" | "squareup" | "cal";

const INTEGRATION_PACKS: IntegrationPack[] = [
  "shopify",
  "calendly",
  "squareup",
  "cal",
];

const PACK_TOOLS: Record<IntegrationPack, { id: string; label: string }[]> = {
  shopify: [
    { id: "list_products", label: "List Products" },
    { id: "add_to_cart", label: "Add to Cart" },
    { id: "remove_from_cart", label: "Remove from Cart" },
    { id: "view_cart", label: "View Cart" },
    { id: "checkout", label: "Checkout" },
    { id: "get_customer_order_history", label: "Order History" },
  ],
  calendly: [
    { id: "get_slots", label: "Get Slots" },
    { id: "get_booking_link", label: "Get Booking Link" },
  ],
  squareup: [
    { id: "list_products", label: "List Products" },
    { id: "manage_cart", label: "Manage Cart (Add/Remove/View)" },
    { id: "checkout", label: "Checkout" },
    { id: "get_customer_order_history", label: "Order History" },
  ],
  cal: [{ id: "get_slots", label: "Get Slots" }],
};

const PACK_LABELS: Record<IntegrationPack, string> = {
  shopify: "Shopify",
  calendly: "Calendly",
  squareup: "Square",
  cal: "Cal.com",
};

const isIntegrationPack = (type: string): type is IntegrationPack =>
  INTEGRATION_PACKS.includes(type as IntegrationPack);

interface IntegrationToolsSectionProps {
  connections: ConnectionDto[];
  tools: AgentTool[];
  onChange: (tools: AgentTool[]) => void;
}

export const IntegrationToolsSection: React.FC<
  IntegrationToolsSectionProps
> = ({ connections, tools, onChange }) => {
  const { t } = useTranslations();

  const connectedIntegrations = useMemo(
    () =>
      connections
        .filter((c) => isIntegrationPack(c.type))
        .map((c) => ({ pack: c.type as IntegrationPack, connection: c })),
    [connections]
  );

  const isPackEnabled = (pack: IntegrationPack) =>
    tools.some((tool) => tool.pack === pack);

  const getToolsForPack = (pack: IntegrationPack) =>
    tools.find((tool) => tool.pack === pack)?.tools ?? [];

  const togglePack = (pack: IntegrationPack, connectionId: string) => {
    if (isPackEnabled(pack)) {
      onChange(tools.filter((tool) => tool.pack !== pack));
    } else {
      onChange([
        ...tools,
        buildIntegrationAgentTool({
          pack,
          connectionId,
          tools: PACK_TOOLS[pack].map((tool) => tool.id),
        }),
      ]);
    }
  };

  const togglePackTool = (
    pack: IntegrationPack,
    toolId: string,
    connectionId: string
  ) => {
    const existing = tools.find((tool) => tool.pack === pack);
    const currentTools = existing?.tools ?? [];

    const newTools = currentTools.includes(toolId)
      ? currentTools.filter((id) => id !== toolId)
      : [...currentTools, toolId];

    const updatedTool = buildIntegrationAgentTool({
      pack,
      connectionId,
      tools: newTools,
    });

    if (existing) {
      onChange(tools.map((tool) => (tool.pack === pack ? updatedTool : tool)));
    } else {
      onChange([...tools, updatedTool]);
    }
  };

  if (connectedIntegrations.length === 0) {
    return (
      <div className="font-secondary text-sm text-dark-600">
        {t({
          id: "cx-agent-setup.no-integrations",
          defaultMessage:
            "No connected integrations. Connect one in the Integrations tab.",
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {connectedIntegrations.map(({ pack, connection }) => (
        <div
          key={connection.id}
          className="flex flex-col gap-3 rounded-xl border border-solid border-[#F2F2F2] p-4"
        >
          <Checkbox
            isChecked={isPackEnabled(pack)}
            onChange={() => togglePack(pack, connection.id)}
            label={
              <span className="font-secondary font-medium">
                {PACK_LABELS[pack]}
                <span className="ml-2 text-xs font-normal text-dark-600">
                  {connection.name}
                </span>
              </span>
            }
          />
          {isPackEnabled(pack) && (
            <div className="ml-6 flex flex-col gap-2">
              {PACK_TOOLS[pack].map((tool) => (
                <Checkbox
                  key={tool.id}
                  isChecked={getToolsForPack(pack).includes(tool.id)}
                  onChange={() => togglePackTool(pack, tool.id, connection.id)}
                  label={tool.label}
                  size="sm"
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
